import { ChangeDetectionStrategy, Component, HostListener, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { NavigationEnd, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs/operators';
import { AppService } from '../../../core/app.service';
import { IssueApiService } from '../../../data/issues/issue-api.service';
import { IconComponent } from '../../../shared/icon/icon.component';
import { IssuesStore } from '../issues.store';
import {
  IssueAnnotation, IssueSeverityId, IssueSnapshot,
  ISSUE_SEVERITIES, issueFmt, issueSev,
} from '../issues.types';
import { IssueIdentityService } from '../issue-identity.service';
import { IssSnapshotComponent } from '../shared/iss-snapshot.component';

/**
 * Global capture overlay + Ctrl/Cmd+Shift+B hotkey. Mounted once in the
 * app shell so any route can be snapshotted without per-page wiring.
 *
 * <p>Flow: hotkey → freeze page + lazy-import html-to-image → open
 * modal with the PNG data URL + auto-captured context (route, viewport,
 * theme, browser, reporter) → agent adds title/description/severity +
 * optional box/arrow → Submit files a new issue into
 * {@link IssuesStore}. Esc cancels; clicking the backdrop cancels.
 */
@Component({
  selector: 'app-issue-reporter',
  standalone: true,
  imports: [CommonModule, TranslateModule, IconComponent, IssSnapshotComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './issue-reporter.component.html',
  styleUrl: '../issues.scss',
})
export class IssueReporterComponent {
  private readonly router = inject(Router);
  private readonly app    = inject(AppService);
  private readonly store  = inject(IssuesStore);
  private readonly api    = inject(IssueApiService);

  readonly open  = signal<boolean>(false);
  readonly flash = signal<boolean>(false);
  readonly shot  = signal<IssueSnapshot | null>(null);
  /** Attachment id returned by the eager upload — we quote it on
   *  submit so the backend can claim the file for the new issue. Null
   *  when capture failed / upload rejected / no snapshot at all. */
  readonly attachmentId = signal<string | null>(null);
  /** In-flight until the eager upload resolves. Blocks Submit so we
   *  don't file the row with a dangling id, but still lets the reporter
   *  keep typing while the upload is on the wire. */
  readonly uploading = signal<boolean>(false);
  readonly annos = signal<IssueAnnotation[]>([]);
  readonly tool  = signal<'box' | 'arrow'>('box');
  readonly title = signal<string>('');
  readonly desc  = signal<string>('');
  readonly sev   = signal<IssueSeverityId>('Medium');
  readonly capturing = signal<boolean>(false);

  readonly severities = ISSUE_SEVERITIES;

  /** Current route path (no leading slash), tracked from the router so
   *  the modal's auto-captured location is always fresh. */
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );
  private route(): string {
    return (this.currentUrl() || '/').replace(/^\//, '').split('?')[0] || 'dashboard';
  }

  /** Auto-captured metadata frozen at the moment of capture. Null when
   *  the modal isn't open (nothing to render). */
  readonly ctx = signal<{
    route: string; crumbs: string[]; hash: string;
    module: string; moduleShort: string;
    env: { viewport: string; theme: string; browser: string };
    when: Date;
  } | null>(null);

  readonly ctxFmt = computed(() => this.ctx() ? issueFmt(this.ctx()!.when) : null);
  /** Real signed-in reporter (falls back to the role-based mock in dev). */
  readonly me = inject(IssueIdentityService).me;
  readonly sevHint = computed(() => issueSev(this.sev()).hint);
  /** Submit is blocked while the eager attachment upload is in-flight
   *  — filing the row before the id lands would drop the screenshot. */
  readonly valid = computed(() => !!this.title().trim() && !!this.desc().trim() && !this.uploading());

  /** Hotkey — Ctrl/Cmd+Shift+B opens capture; Esc closes.
   *  Bound at the document level so it fires from any focus target. */
  @HostListener('document:keydown', ['$event'])
  onKey(e: KeyboardEvent): void {
    const isB = e.code === 'KeyB' || e.key === 'b' || e.key === 'B';
    if ((e.metaKey || e.ctrlKey) && e.shiftKey && isB) {
      e.preventDefault();
      if (!this.open() && !this.capturing()) this.capture();
    } else if (e.key === 'Escape' && this.open()) {
      this.open.set(false);
    }
  }

  /** localStorage key for the capture-area preference. */
  private static readonly SCOPE_KEY = 'iss.capture.viewportOnly';

  /**
   * Capture only what is on screen, instead of the whole scrollable page.
   *
   * <p>A full-page shot is the right default for a bug about layout or about something far down
   * the page, but on a long screen — a bookings list, a booking's detail tabs — it produces a
   * tall image where the thing being reported is a sliver, and annotating it is awkward.
   *
   * <p>Defaults to ON. A reporter is nearly always describing something they can see, and a
   * full-page shot of a long screen is both slower to produce and larger to upload — large enough
   * to be refused outright by the proxy in front of issue-service.
   *
   * <p>Remembered per viewer, because whoever prefers one almost always prefers it every time.
   * Wrapped: localStorage throws in a private window and comes back empty with site data
   * cleared, and neither should stop a bug being reported — an unreadable preference just means
   * the default.
   */
  readonly viewportOnly = signal<boolean>(IssueReporterComponent.readScopePref());

  private static readScopePref(): boolean {
    try {
      // Explicit '0' is the only thing that turns it off: absent means "never chosen", which is
      // the default, and anything else is a value we did not write.
      return localStorage.getItem(IssueReporterComponent.SCOPE_KEY) !== '0';
    } catch {
      return true;
    }
  }

  /** Flip the capture area and re-shoot, so the preview matches the choice immediately. */
  async setViewportOnly(on: boolean): Promise<void> {
    if (this.viewportOnly() === on) return;
    this.viewportOnly.set(on);
    try {
      localStorage.setItem(IssueReporterComponent.SCOPE_KEY, on ? '1' : '0');
    } catch { /* preference is a convenience; losing it must not break the capture */ }
    // Re-capture keeps any annotations meaningless against the new image, so they are dropped
    // rather than left pointing at the wrong place.
    await this.recapture();
  }

  /** The page's own painted background, for a capture rooted at <body>. */
  private pageBackground(): string {
    for (const el of [document.querySelector('.app'), document.body, document.documentElement]) {
      if (!el) continue;
      const bg = getComputedStyle(el as HTMLElement).backgroundColor;
      if (bg && bg !== 'transparent' && !/rgba\(0,\s*0,\s*0,\s*0\)/.test(bg)) return bg;
    }
    return '#ffffff';
  }

  async capture(): Promise<void> {
    if (this.capturing()) return;
    this.capturing.set(true);
    // Freeze context first so the reporter always knows where they
    // were, even if the html-to-image call fails (large DOMs, cross-
    // origin images with CORS blocks, etc).
    const crumbs = this.crumbsForRoute();
    const route = this.route();
    const ua = navigator.userAgent.match(/(Firefox|Edg|Chrome|Safari)\/?\s*([\d.]+)/);
    const browser = ua ? `${ua[1] === 'Edg' ? 'Edge' : ua[1]} ${parseInt(ua[2], 10)}` : 'Browser';
    const module = crumbs.length ? crumbs[crumbs.length - 1] : route;
    const moduleShort = crumbs.length > 1
      ? `${crumbs[crumbs.length - 1]} · ${crumbs[crumbs.length - 2]}`
      : module;
    this.ctx.set({
      route,
      crumbs,
      hash: window.location.hash || '#' + route,
      module,
      moduleShort,
      env: {
        viewport: `${window.innerWidth} × ${window.innerHeight}`,
        theme: document.documentElement.getAttribute('data-theme') || 'light',
        browser,
      },
      when: new Date(),
    });
    this.annos.set([]); this.tool.set('box');
    this.title.set(''); this.desc.set(''); this.sev.set('Medium');
    // Drop any previously uploaded screenshot BEFORE the new shot: if this capture fails, the
    // report must go out with no attachment rather than with the last one's image.
    this.attachmentId.set(null);
    this.flash.set(true); setTimeout(() => this.flash.set(false), 260);

    const { snap, blob: capturedBlob } = await this.shoot();
    this.shot.set(snap);
    this.open.set(true);
    this.capturing.set(false);
    this.upload(capturedBlob);
  }

  /**
   * Rasterise the page to a PNG.
   *
   * <p>Shared by the first capture and by a re-shoot after the capture area changes, so the two
   * cannot drift — a second copy of this would eventually frame or size the image differently
   * from the one the annotations were placed on.
   */
  private async shoot(): Promise<{ snap: IssueSnapshot | null; blob: Blob | null }> {
    // Lazy-load html-to-image — pays the ~40kB only on first capture,
    // and never in test builds that don't touch the reporter.
    let snap: IssueSnapshot | null = null;
    let capturedBlob: Blob | null = null;
    try {
      // Angular Material appends `.cdk-overlay-container` to <body>, as a SIBLING of the shell —
      // so a dialog or drawer is outside `.app` and was missing from every screenshot taken with
      // one open, which is exactly when an agent reports a bug about it. Capture <body> in that
      // case so the overlay and its backdrop are included; keep the narrower `.app` target
      // otherwise, since it is the cheaper and better-tested path.
      const overlayOpen = !!document.querySelector(
        '.cdk-overlay-container .cdk-overlay-pane, .cdk-overlay-container .cdk-dialog-container',
      );
      const target = (overlayOpen
        ? document.body
        : document.querySelector('.app')) as HTMLElement | null;
      if (target) {
        const { toPng } = await import('html-to-image');
        // Capture one PNG and use it for both the immediate preview and the
        // upload. Running toBlob() and toPng() in parallel made the preview
        // disappear when either conversion failed on a complex page.
        const viewportOnly = this.viewportOnly();
        const opts: Record<string, unknown> = {
          // `cacheBust: true` appends a unique query to every image URL, which defeats the
          // browser cache and re-downloads each one over the network — on an image-heavy screen
          // (hotel results, property photos) that was most of the capture's wall-clock time. The
          // images are already loaded and same-origin or CORS-cleared by the time anyone presses
          // the hotkey, so re-fetching them buys nothing.
          cacheBust: false,
          pixelRatio: 1,
          // Remote/web-font loading is the most common reason html-to-image
          // rejects a large application shell. The screenshot remains useful
          // with the browser's fallback fonts.
          skipFonts: true,
          // <body> has no painted background of its own, so a body-target capture would come out
          // with a transparent backdrop that reads as black in most PNG viewers.
          backgroundColor: overlayOpen || viewportOnly ? this.pageBackground() : undefined,
          // Never capture the reporter's own capture UI.
          //
          // Deliberately NOT also filtering out off-screen nodes for a viewport shot, tempting as
          // that is for speed: html-to-image drops filtered nodes from the clone, so removing
          // everything above the fold reflows the visible content up to the clone's origin, and
          // the offset below then shifts it straight off the canvas — a blank screenshot on any
          // scrolled page. Framing and pruning cannot both be done this way.
          filter: (n: HTMLElement) => !n.classList?.contains?.('iss-overlay'),
        };

        // Viewport-only: clip the render to the window box.
        //
        // html-to-image always rasterises the WHOLE target element, so there is no "capture the
        // visible part" option — the way to get one is to render at the window's size and shift
        // the content up/left by the current scroll offset, so everything above and left of the
        // fold falls outside the canvas. The target stays the same element; only the framing
        // changes.
        const r = target.getBoundingClientRect();
        if (viewportOnly) {
          opts['width'] = window.innerWidth;
          opts['height'] = window.innerHeight;
          opts['style'] = {
            // `r.top/left` rather than scrollX/scrollY: this is the target's offset from the
            // viewport, which already accounts for page scroll AND for a target that does not
            // start at the document origin (the shell has a topbar, and `.app` is not <body>).
            transform: `translate(${Math.round(r.left)}px, ${Math.round(r.top)}px)`,
            transformOrigin: 'top left',
          };
        }

        const dataUrl = await toPng(target, opts as Parameters<typeof toPng>[1]);
        // The viewer stretches the PNG to w x h and places annotations as fractions of that box,
        // so these must describe the image that was actually captured. A <body> target can be
        // taller than the viewport, and reporting innerHeight for it would squash the image and
        // pull every annotation away from the thing it points at.
        const w = viewportOnly ? window.innerWidth : Math.round(r.width);
        const h = viewportOnly
            ? window.innerHeight
            : (overlayOpen ? Math.round(r.height) || window.innerHeight : window.innerHeight);
        snap = { dataUrl, w, h, scrollY: Math.round(window.scrollY) };
        capturedBlob = this.dataUrlToBlob(dataUrl);
      }
    } catch {
      // Non-fatal: the modal opens with the "No page preview"
      // placeholder + the auto-captured context.
      snap = null;
    }
    return { snap, blob: capturedBlob };
  }

  /**
   * Re-shoot the page without disturbing the report being written.
   *
   * <p>Used when the capture area is switched from inside the modal. The title, description and
   * severity are deliberately kept — the reporter has usually typed them by then — while the
   * annotations are dropped, because they are stored as fractions of the image box and would
   * point somewhere else entirely on a differently-framed shot.
   *
   * <p>The reporter's own overlay is excluded by the same `filter` the first capture uses, so
   * shooting with the modal open does not photograph the modal.
   */
  private async recapture(): Promise<void> {
    if (this.capturing()) return;
    this.capturing.set(true);
    this.annos.set([]);
    this.attachmentId.set(null);
    const { snap, blob } = await this.shoot();
    this.shot.set(snap);
    this.capturing.set(false);
    this.upload(blob);
  }

  /**
   * Send the PNG to issue-service.
   *
   * <p>Fired as soon as the bytes exist — the reporter is still reading the modal while this is
   * in flight, so submit() rarely waits on it. A failure only drops the screenshot; the issue
   * itself still lands through the create endpoint.
   */
  private upload(blob: Blob | null): void {
    if (!blob) return;
    this.uploading.set(true);
    this.api.uploadAttachment(blob).subscribe({
      next: (res) => {
        this.attachmentId.set(res.id);
        this.uploading.set(false);
      },
      error: () => {
        this.attachmentId.set(null);
        this.uploading.set(false);
      },
    });
  }

  /** Convert the exact PNG shown in the preview into the upload payload. */
  private dataUrlToBlob(dataUrl: string): Blob | null {
    try {
      const comma = dataUrl.indexOf(',');
      if (comma < 0) return null;
      const meta = dataUrl.slice(0, comma);
      const raw = atob(dataUrl.slice(comma + 1));
      const bytes = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
      const type = meta.match(/^data:([^;]+)/)?.[1] || 'image/png';
      return new Blob([bytes], { type });
    } catch {
      return null;
    }
  }

  /** Pull the crumbs stored on the current route's data — matches how
   *  the topbar's breadcrumb reads them, so what we capture is what the
   *  user actually sees in the chrome. */
  private crumbsForRoute(): string[] {
    let r = this.router.routerState.snapshot.root;
    let crumbs: string[] = [];
    while (r) {
      const c = r.data?.['crumbs'];
      if (Array.isArray(c)) crumbs = c;
      if (!r.firstChild) break;
      r = r.firstChild;
    }
    return crumbs;
  }

  submit(): void {
    if (!this.valid() || !this.ctx()) return;
    const c = this.ctx()!;
    const id = this.store.create({
      title: this.title().trim(),
      description: this.desc().trim(),
      severity: this.sev(),
      reporterId: this.me().id,
      location: { route: c.route, crumbs: c.crumbs, hash: c.hash, module: c.moduleShort },
      env: c.env,
      snapshot: this.shot(),
      // Id returned by the eager upload — the backend uses it to link
      // the pre-staged attachment row to the new issue. Local {@link
      // snapshot} keeps the data URL for the optimistic in-modal
      // render until the presigned GET on the detail view takes over.
      snapshotAttachmentId: this.attachmentId(),
      annotations: this.annos(),
    }).id;
    this.attachmentId.set(null);
    this.open.set(false);
    this.app.toastKey('issues.toasts.reported', { code: id });
  }

  backdrop(e: MouseEvent): void {
    if (e.target === e.currentTarget) this.open.set(false);
  }
  addAnno(a: IssueAnnotation): void { this.annos.update((list) => [...list, a]); }
  undoAnno(): void { this.annos.update((list) => list.slice(0, -1)); }
}
